import type { AuthenticatedUser, MeResponse } from '@manikin-overmind/protocol';
import { useCallback, useEffect, useState } from 'preact/hooks';

export type AuthStatus = 'checking' | 'authenticated' | 'redirecting' | 'error';

export type AuthState =
  | { status: 'checking' | 'redirecting' | 'error'; user: null; retry: () => void }
  | { status: 'authenticated'; user: AuthenticatedUser; retry: () => void };

type InternalState =
  | { status: 'checking' | 'redirecting' | 'error'; user: null }
  | { status: 'authenticated'; user: AuthenticatedUser };

let redirectStarted = false;

export function redirectToSignIn(): void {
  if (redirectStarted) return;
  redirectStarted = true;
  const redirectTo = window.location.pathname + window.location.search;
  window.location.assign(`/auth/signin?redirectTo=${encodeURIComponent(redirectTo)}`);
}

export async function authenticatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, { ...init, credentials: 'same-origin' });
  if (response.status === 401) {
    redirectToSignIn();
    throw new Error('Your session expired. Redirecting to sign in.');
  }
  return response;
}

export async function sessionIsAuthenticated(): Promise<boolean | null> {
  try {
    const response = await fetch('/api/me', { credentials: 'same-origin' });
    if (response.status === 401) return false;
    if (!response.ok) return null;
    const body = (await response.json()) as MeResponse;
    return body.account !== null;
  } catch {
    return null;
  }
}

export function useAuth(): AuthState {
  const [state, setState] = useState<InternalState>({ status: 'checking', user: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'checking', user: null });
    fetch('/api/me', { credentials: 'same-origin' })
      .then((response) => {
        if (response.status === 401) {
          if (!cancelled) setState({ status: 'redirecting', user: null });
          redirectToSignIn();
          return null;
        }
        if (!response.ok) throw new Error(`GET /api/me returned ${response.status}`);
        return response.json() as Promise<MeResponse>;
      })
      .then((body) => {
        if (cancelled || body === null) return;
        if (!body.account) {
          setState({ status: 'redirecting', user: null });
          redirectToSignIn();
          return;
        }
        setState({ status: 'authenticated', user: body.account });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('failed to check sign-in status', error);
        setState({ status: 'error', user: null });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { ...state, retry };
}
